<script setup>
import { ref, watch, computed, useTemplateRef } from "vue";
import { onClickOutside, useEventListener } from "@vueuse/core";
import { useI18n } from "vue-i18n";
import SvgIcon from "@/shared/ui/svg-icon/SvgIcon.vue";
import StepperItem from "./StepperItem.vue";

/**
 * Production-ready dynamic stepper panel.
 * Designed to be feature-agnostic — steps + state come from caller.
 *
 * Example:
 *   <Stepper
 *     v-model:open="open"
 *     v-model:expanded-key="expandedKey"
 *     :steps="steps"
 *     @step-action="handleAction"
 *   />
 */
const props = defineProps({
  open: { type: Boolean, default: false },
  expandedKey: { type: String, default: null },
  steps: { type: Array, required: true },
  introText: { type: String, default: "" },
  autoExpandCurrent: { type: Boolean, default: true },
  closeOnAction: { type: Boolean, default: true },
  closeOnClickOutside: { type: Boolean, default: true },
  ignoreClickOutsideSelectors: {
    type: Array,
    default: () => [".stepper-trigger"],
  },
  width: { type: String, default: "380px" },
  position: {
    type: String,
    default: "left",
    validator: (v) => v === "left" || v === "right",
  },
  doneCount: { type: Number, default: 0 },
  totalSteps: { type: Number, default: 0 },
  progress: { type: Number, default: 0 },
  showProgress: { type: Boolean, default: true },
});

const emit = defineEmits([
  "update:open",
  "update:expanded-key",
  "step-action",
  "close",
]);

const { t } = useI18n();

const panelRef = useTemplateRef("panelRef");
const lastCurrentKey = ref(null);

const currentStepKey = computed(() => {
  const current = props.steps.find((s) => s.status === "current");
  if (current) return current.key;
  const firstWithDetails = props.steps.find((s) => {
    const d = s.details;
    return d && ((d.todo && d.todo.length) || d.warning);
  });
  return firstWithDetails ? firstWithDetails.key : null;
});

const resolvedIntro = computed(
  () => props.introText || t("Чтобы продолжить, выполните все шаги ниже.")
);

const styleVars = computed(() => ({
  "--stepper-width": props.width,
}));

const positionClass = computed(() =>
  props.position === "right" ? "stepper--right" : "stepper--left"
);

watch(
  () => props.open,
  (val) => {
    if (val) {
      if (props.autoExpandCurrent) {
        emit("update:expanded-key", currentStepKey.value);
      }
      lastCurrentKey.value = currentStepKey.value;
    } else {
      emit("update:expanded-key", null);
      lastCurrentKey.value = null;
    }
  },
  { immediate: true }
);

watch(currentStepKey, (newKey) => {
  if (!props.open || !props.autoExpandCurrent) return;
  if (!newKey || newKey === lastCurrentKey.value) return;
  emit("update:expanded-key", newKey);
  lastCurrentKey.value = newKey;
});

onClickOutside(
  panelRef,
  () => {
    if (props.open && props.closeOnClickOutside) {
      emit("update:open", false);
      emit("close");
    }
  },
  { ignore: props.ignoreClickOutsideSelectors }
);

useEventListener(document, "keydown", (e) => {
  if (!props.open) return;
  if (e.key === "Escape" || e.key === "Esc") {
    emit("update:open", false);
    emit("close");
  }
});

function onToggle(step) {
  const next = props.expandedKey === step.key ? null : step.key;
  emit("update:expanded-key", next);
}

function onAction(step) {
  emit("step-action", step);
  if (props.closeOnAction) {
    emit("update:open", false);
    emit("close");
  }
}

function onClose() {
  emit("update:open", false);
  emit("close");
}
</script>

<template>
  <transition name="stepper-panel">
    <aside
      v-if="open"
      ref="panelRef"
      class="stepper"
      :class="positionClass"
      role="region"
      :aria-label="resolvedIntro"
      :style="styleVars"
    >
      <div class="stepper__inner">
        <div class="stepper__header">
          <div class="stepper__intro">
            <SvgIcon
              name="sli-info"
              class="stepper__intro-icon"
              style="fill: none"
            />
            <div class="stepper__intro-text">{{ resolvedIntro }}</div>
          </div>
          <button
            type="button"
            class="stepper__close"
            :aria-label="t('Закрыть')"
            @click="onClose"
          >
            <SvgIcon name="huge-close" />
          </button>
        </div>

        <div v-if="showProgress" class="stepper__progress">
          <div class="stepper__progress-meta">
            <div class="stepper__progress-label">{{ t("Прогресс") }}</div>
            <div class="stepper__progress-counter">
              {{ doneCount }} / {{ totalSteps }}
            </div>
          </div>
          <div class="stepper__progress-bar">
            <div
              class="stepper__progress-fill"
              :style="{ width: progress + '%' }"
            />
          </div>
        </div>

        <div class="stepper__list">
          <StepperItem
            v-for="(step, index) in steps"
            :key="step.key"
            :step="step"
            :is-last="index === steps.length - 1"
            :expanded="expandedKey === step.key"
            @toggle="onToggle"
            @action="onAction"
          />
        </div>

        <div class="stepper__decoration" aria-hidden="true">
          <svg
            viewBox="0 0 320 160"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g opacity="0.15">
              <path
                d="M40 80 L80 60 L120 80 L80 100 Z"
                stroke="currentColor"
                stroke-width="1.2"
                fill="none"
              />
              <path
                d="M80 60 L80 20 L120 40 L120 80"
                stroke="currentColor"
                stroke-width="1.2"
                fill="none"
              />
              <path
                d="M40 80 L40 40 L80 20"
                stroke="currentColor"
                stroke-width="1.2"
                fill="none"
              />
              <path
                d="M160 110 L200 90 L240 110 L200 130 Z"
                stroke="currentColor"
                stroke-width="1.2"
                fill="none"
              />
              <path
                d="M200 90 L200 50 L240 70 L240 110"
                stroke="currentColor"
                stroke-width="1.2"
                fill="none"
              />
              <path
                d="M160 110 L160 70 L200 50"
                stroke="currentColor"
                stroke-width="1.2"
                fill="none"
              />
              <path
                d="M240 60 L280 40 L320 60 L280 80 Z"
                stroke="currentColor"
                stroke-width="1.2"
                fill="none"
              />
            </g>
          </svg>
        </div>
      </div>
    </aside>
  </transition>
</template>

<style scoped>
.stepper {
  position: absolute;
  top: 8px;
  bottom: 8px;
  width: var(--stepper-width, 380px);
  padding: 0 8px;
  z-index: 20;
  overflow: visible;
  pointer-events: none;
}

.stepper--left {
  left: 8px;
}

.stepper--right {
  right: 8px;
}

.stepper__inner {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  pointer-events: auto;
  border: 1px solid hsl(var(--border));
  border-radius: var(--radius);
  background: hsl(var(--card));
  background-image: linear-gradient(
    180deg,
    rgba(99, 102, 241, 0.05) 0%,
    transparent 70%
  );
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12),
    0 2px 6px rgba(15, 23, 42, 0.06);
  overflow: hidden;
}

.stepper__inner > * {
  padding-left: 18px;
  padding-right: 18px;
}

.stepper__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  padding-top: 16px;
  padding-bottom: 12px;
  border-bottom: 1px dashed hsl(var(--border));
  flex-shrink: 0;
}

.stepper__intro {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  flex: 1;
}

.stepper__intro-icon {
  width: 14px;
  height: 14px;
  color: #6366f1;
  flex-shrink: 0;
  margin-top: 2px;
}

.stepper__intro-text {
  font-size: 12px;
  font-weight: 500;
  line-height: 1.45;
  color: hsl(var(--muted-foreground));
}

.stepper__close {
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: hsl(var(--muted-foreground));
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 150ms ease, color 150ms ease;
  flex-shrink: 0;
}

.stepper__close:hover {
  background: hsl(var(--muted));
  color: hsl(var(--foreground));
}

.stepper__close:focus-visible {
  outline: 2px solid hsl(var(--ring, var(--primary)));
  outline-offset: 2px;
}

.stepper__close :deep(.svg-icon) {
  width: 14px;
  height: 14px;
}

.stepper__progress {
  padding-top: 12px;
  padding-bottom: 14px;
  flex-shrink: 0;
}

.stepper__progress-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.stepper__progress-label {
  font-size: 11px;
  font-weight: 500;
  color: hsl(var(--muted-foreground));
  letter-spacing: 0.01em;
}

.stepper__progress-counter {
  font-size: 12px;
  font-weight: 600;
  color: hsl(var(--foreground));
}

.stepper__progress-bar {
  height: 4px;
  border-radius: 999px;
  background: rgba(99, 102, 241, 0.15);
  overflow: hidden;
}

.stepper__progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%);
  border-radius: 999px;
  transition: width 600ms cubic-bezier(0.16, 1, 0.3, 1);
  box-shadow: 0 0 8px rgba(99, 102, 241, 0.35);
}

.stepper__list {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-top: 4px;
  padding-bottom: 16px;
  scrollbar-width: thin;
  scrollbar-color: hsl(var(--muted-foreground) / 0.5) transparent;
}

.stepper__list::-webkit-scrollbar {
  width: 6px;
}

.stepper__list::-webkit-scrollbar-track {
  background: transparent;
}

.stepper__list::-webkit-scrollbar-thumb {
  background: hsl(var(--muted-foreground) / 0.4);
  border-radius: 999px;
}

.stepper__list::-webkit-scrollbar-thumb:hover {
  background: hsl(var(--muted-foreground) / 0.7);
}

.stepper__decoration {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 110px;
  pointer-events: none;
  z-index: 0;
  opacity: 0.7;
  color: #6366f1;
}

.stepper__decoration svg {
  width: 100%;
  height: 100%;
}

.stepper-panel-enter-active {
  transition: transform 0.4s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.3s cubic-bezier(0.22, 1, 0.36, 1);
  will-change: transform, opacity;
}

.stepper-panel-leave-active {
  transition: transform 0.3s cubic-bezier(0.4, 0, 1, 1),
    opacity 0.25s cubic-bezier(0.4, 0, 1, 1);
  will-change: transform, opacity;
}

.stepper--left.stepper-panel-enter-from,
.stepper--left.stepper-panel-leave-to {
  transform: translateX(-20px);
  opacity: 0;
}

.stepper--right.stepper-panel-enter-from,
.stepper--right.stepper-panel-leave-to {
  transform: translateX(20px);
  opacity: 0;
}

.stepper-panel-enter-to,
.stepper-panel-leave-from {
  transform: translateX(0);
  opacity: 1;
}

@media (prefers-reduced-motion: reduce) {
  .stepper-panel-enter-active,
  .stepper-panel-leave-active {
    transition: opacity 0.15s ease;
  }
  .stepper--left.stepper-panel-enter-from,
  .stepper--left.stepper-panel-leave-to,
  .stepper--right.stepper-panel-enter-from,
  .stepper--right.stepper-panel-leave-to {
    transform: none;
  }
  .stepper__progress-fill {
    transition: none;
  }
}
</style>
